import { useState } from "react";
import { Info } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface Props {
  title: string;
  body: string;
  nextStep?: string;
  className?: string;
  align?: "start" | "center" | "end";
  /** Optional trigger override; defaults to a tiny info icon. */
  children?: React.ReactNode;
}

/**
 * Tap/hover explainer. Uses Popover (touch + hover) so it works on
 * mobile and desktop without depending on hover-only tooltips.
 */
export default function InfoTip({ title, body, nextStep, className, align = "end", children }: Props) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        asChild
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        {children ?? (
          <button
            type="button"
            aria-label={`Explain ${title}`}
            className={cn(
              "inline-flex h-5 w-5 items-center justify-center rounded-full border border-white/10 bg-background/40 text-muted-foreground transition hover:text-primary",
              className,
            )}
          >
            <Info className="h-3 w-3" />
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent align={align} className="w-72 rounded-2xl border-primary/20 bg-card/95 p-4 text-xs leading-relaxed text-foreground/90 backdrop-blur-xl">
        <p className="font-heading text-sm font-semibold text-foreground">{title}</p>
        <p className="mt-1.5 text-muted-foreground">{body}</p>
        {nextStep && (
          <p className="mt-2 rounded-xl border border-primary/20 bg-primary/[0.06] px-2.5 py-1.5 text-[11px] text-foreground/90">
            <span className="font-semibold text-primary">Next step: </span>
            {nextStep}
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
}